import { useState, useEffect, useRef } from "react";
import "./Contact.css";

const faqs = [
  {
    q: "How do I post a job listing on TalentHive?",
    a: "Register as an employer, verify your company profile, and click 'Post a Job' from your dashboard. Listings go live within 24 hours after our review.",
  },
  {
    q: "Is TalentHive free for job seekers?",
    a: "Yes! Creating a profile, uploading your resume, and applying to jobs is completely free for candidates. Premium features like profile boosting are available optionally.",
  },
  {
    q: "How do I report a fraudulent job posting?",
    a: "Click the 'Report' flag on any listing or email us at abuse@talenthive.com. Our trust & safety team reviews all reports within 12 hours.",
  },
  {
    q: "Can I edit my application after submitting?",
    a: "Applications cannot be edited once submitted, but you can withdraw and reapply if the position is still open. Contact support if you need urgent assistance.",
  },
  {
    q: "How long does account verification take?",
    a: "Email verification is instant. Business account verification takes 1–2 business days while our team reviews your company documents.",
  },
];

const contactCards = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <polyline points="22,6 12,13 2,6"/>
      </svg>
    ),
    label: "General Enquiries",
    value: "hello@talenthive.com",
    sub: "We reply within 24 hours",
    href: "mailto:hello@talenthive.com",
    tag: "Email",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .94h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
      </svg>
    ),
    label: "Phone Support",
    value: "+91 98100 44015",
    sub: "Mon–Fri, 10am–7pm IST",
    href: "tel:+919810045678",
    tag: "Call",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
        <circle cx="12" cy="10" r="3"/>
      </svg>
    ),
    label: "Office Address",
    value: "Punjabi Bagh West",
    sub: "Delhi 110026, India",
    href: "https://maps.app.goo.gl/BWAnq7hkVVu2dFdM9",
    tag: "Visit",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
      </svg>
    ),
    label: "Live Chat",
    value: "Chat with our team",
    sub: "Avg. response: 3 minutes",
    href: "#chat",
    tag: "Chat",
  },
];

const departments = [
  { id: "general", label: "General" },
  { id: "jobs", label: "Job Listings" },
  { id: "billing", label: "Billing" },
  { id: "technical", label: "Technical" },
  { id: "partnership", label: "Partnership" },
  { id: "press", label: "Press / Media" },
];

export default function ContactUs() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", dept: "general", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const floatRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Parallax float effect on hero shapes
  useEffect(() => {
    const onMove = (e) => {
      if (!floatRef.current) return;
      const x = (e.clientX / window.innerWidth - 0.5) * 18;
      const y = (e.clientY / window.innerHeight - 0.5) * 12;
      floatRef.current.style.transform = `translate(${x}px, ${y}px)`;
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.email = "Enter a valid email";
    if (!form.subject.trim()) e.subject = "Subject is required";
    if (form.message.trim().length < 20) e.message = "Message must be at least 20 characters";
    return e;
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  const errs = validate();
  if (Object.keys(errs).length) { setErrors(errs); return; }
  setErrors({});
  setLoading(true);
  try {
    const response = await fetch("http://localhost:3000/api/support/contact", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: form.name,
        email: form.email,
        dept: form.dept,
        subject: form.subject,
        message: form.message,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error("Contact form error:", data);
      alert("Failed to send message: " + (data.error || "Unknown error"));
      return;
    }

    setSubmitted(true);
  } catch (err) {
    console.error("Contact form network error:", err);
    alert("Network error — make sure the server is running on port 5001");
  } finally {
    setLoading(false);
  }
};



  const handleChange = (field, val) => {
    setForm((f) => ({ ...f, [field]: val }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
  };

  return (
    <div className="cu-root">

      {/* Hero */}
      <header className="cu-hero" data-aos="fade-up">
        <div className="cu-hero-shapes" ref={floatRef}>
          <div className="cu-shape cu-shape--hex" />
          <div className="cu-shape cu-shape--ring" />
          <div className="cu-shape cu-shape--dot-grid" />
          <div className="cu-shape cu-shape--blob" />
        </div>
        <div className="cu-hero-content">
          <div className="cu-hero-pill">
            <span className="cu-pill-dot" />
            We're here to help
          </div>
          <h1 className="cu-hero-title">
            Let's <span className="cu-orange">Connect</span>
          </h1>
          <p className="cu-hero-sub">
            Whether you're a job seeker, employer, or just curious — our team is ready to help you find your next opportunity or perfect hire.
          </p>
          <div className="cu-hero-stats">
            <div className="cu-stat"><strong>3 min</strong><span>Avg. response</span></div>
            <div className="cu-stat-divider" />
            <div className="cu-stat"><strong>24 / 7</strong><span>Email support</span></div>
            <div className="cu-stat-divider" />
            <div className="cu-stat"><strong>98%</strong><span>Satisfaction rate</span></div>
          </div>
        </div>
        <div className="cu-hero-wave">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#ffffff"/>
          </svg>
        </div>
      </header>

      {/* Contact Cards */}
      <section className="cu-cards-section" data-aos="fade-up">
        <div className="cu-cards-grid">
          {contactCards.map((card, i) => (
            <a href={card.href} key={i} className="cu-card" data-aos="fade-up" style={{ "--delay": `${i * 0.08}s` }}>
              <div className="cu-card-top">
                <div className="cu-card-icon">{card.icon}</div>
                <span className="cu-card-tag">{card.tag}</span>
              </div>
              <p className="cu-card-label">{card.label}</p>
              <p className="cu-card-value">{card.value}</p>
              <p className="cu-card-sub">{card.sub}</p>
              <div className="cu-card-arrow">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 10h12M11 5l5 5-5 5"/>
                </svg>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Form + Info */}
      <section className="cu-form-section" data-aos="fade-up">
        <div className="cu-form-wrap">
          {/* Left Info */}
          <div className="cu-info-col">
            <div className="cu-info-label">Send us a message</div>
            <h2 className="cu-info-title">We'd love to<br />hear from you</h2>
            <p className="cu-info-desc">
              Fill in the form and our team will get back to you within one business day. For urgent matters, call us directly or use live chat.
            </p>

            <div className="cu-info-hours">
              <div className="cu-hours-title">Support Hours</div>
              <div className="cu-hours-row"><span>Mon – Fri</span><span>10:00 AM – 7:00 PM IST</span></div>
              <div className="cu-hours-row"><span>Saturday</span><span>11:00 AM – 4:00 PM IST</span></div>
              <div className="cu-hours-row cu-hours-closed"><span>Sunday</span><span>Closed</span></div>
            </div>

            <div className="cu-social-row">
              {["LinkedIn", "Twitter", "Instagram"].map((s) => (
                <a href="#" key={s} className="cu-social-chip">{s}</a>
              ))}
            </div>

            <div className="cu-info-pattern" aria-hidden="true">
              {Array.from({ length: 48 }).map((_, i) => (
                <span key={i} />
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="cu-form-col">
            {submitted ? (
              <div className="cu-success">
                <div className="cu-success-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h3>Message Sent!</h3>
                <p>Thanks for reaching out, <strong>{form.name}</strong>. We'll reply to <strong>{form.email}</strong> within 24 hours.</p>
                <button className="cu-btn cu-btn--primary" onClick={() => { setSubmitted(false); setForm({ name: "", email: "", dept: "general", subject: "", message: "" }); }}>
                  Send Another
                </button>
              </div>
            ) : (
              <form className="cu-form" onSubmit={handleSubmit} noValidate>
                <div className="cu-form-row">
                  <div className={`cu-field ${errors.name ? "cu-field--error" : ""}`}>
                    <label>Full Name</label>
                    <input
                      type="text"
                      placeholder="Arjun Sharma"
                      value={form.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                    />
                    {errors.name && <span className="cu-error">{errors.name}</span>}
                  </div>
                  <div className={`cu-field ${errors.email ? "cu-field--error" : ""}`}>
                    <label>Email Address</label>
                    <input
                      type="email"
                      placeholder="arjun@company.com"
                      value={form.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                    />
                    {errors.email && <span className="cu-error">{errors.email}</span>}
                  </div>
                </div>

                <div className="cu-field">
                  <label>Department</label>
                  <div className="cu-dept-grid">
                    {departments.map((d) => (
                      <button
                        type="button"
                        key={d.id}
                        className={`cu-dept-chip ${form.dept === d.id ? "cu-dept-chip--active" : ""}`}
                        onClick={() => handleChange("dept", d.id)}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={`cu-field ${errors.subject ? "cu-field--error" : ""}`}>
                  <label>Subject</label>
                  <input
                    type="text"
                    placeholder="What can we help you with?"
                    value={form.subject}
                    onChange={(e) => handleChange("subject", e.target.value)}
                  />
                  {errors.subject && <span className="cu-error">{errors.subject}</span>}
                </div>

                <div className={`cu-field ${errors.message ? "cu-field--error" : ""}`}>
                  <label>Message</label>
                  <textarea
                    rows={5}
                    placeholder="Describe your question or issue in detail..."
                    value={form.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                  />
                  <span className="cu-char-count">{form.message.length} / 1000</span>
                  {errors.message && <span className="cu-error">{errors.message}</span>}
                </div>

                <button type="submit" className={`cu-btn cu-btn--primary cu-btn--full ${loading ? "cu-btn--loading" : ""}`} disabled={loading}>
                  {loading ? (
                    <span className="cu-spinner" />
                  ) : (
                    <>
                      Send Message
                      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="2" y1="10" x2="18" y2="10"/>
                        <polyline points="13 5 18 10 13 15"/>
                      </svg>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Map placeholder */}
      <section className="cu-map-section" data-aos="fade-up">
        <div className="cu-map-inner">
          <div className="cu-map-info">
            <div className="cu-map-badge">Our Office</div>
            <h3>Find Us in Delhi</h3>
            <p>Punjabi Bagh West, Delhi<br />Delhi, 110026, India</p>
            <a href="https://maps.app.goo.gl/BWAnq7hkVVu2dFdM9" target="_blank" rel="noreferrer" className="cu-btn cu-btn--outline">
              Open in Maps
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M10 3H5a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-5"/>
                <polyline points="15 3 20 3 20 8"/>
                <line x1="10" y1="10" x2="20" y2="3"/>
              </svg>
            </a>
          </div>
  <div className="cu-map-visual">
<iframe 
  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d8688.349219468028!2d77.11881069596808!3d28.663705466633218!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d0396e99a6f9b%3A0xe125214ba794b2fc!2sPunjabi%20Bagh%2C%20Delhi!5e1!3m2!1sen!2sin!4v1775823465018!5m2!1sen!2sin"
  width="100%"
  height="100%"
  style={{ border: 0, borderRadius: "12px" }}
  allowFullScreen
  loading="lazy"
  referrerPolicy="no-referrer-when-downgrade"
/>
</div>
        </div>
      </section>

      {/* FAQ */}
      <section className="cu-faq-section" data-aos="fade-up">
        <div className="cu-faq-inner">
          <div className="cu-faq-head">
            <span className="cu-section-eyebrow">FAQ</span>
            <h2>Frequently Asked<br /><span className="cu-orange">Questions</span></h2>
            <p>Can't find what you're looking for? Reach out to our team directly.</p>
          </div>
          <div className="cu-faq-list">
            {faqs.map((faq, i) => (
              <div key={i} className={`cu-faq-item ${activeFaq === i ? "cu-faq-item--open" : ""}`}>
                <button className="cu-faq-q" onClick={() => setActiveFaq(activeFaq === i ? null : i)}>
                  <span>{faq.q}</span>
                  <div className="cu-faq-chevron">
                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="5 8 10 13 15 8" />
                    </svg>
                  </div>
                </button>
                <div className="cu-faq-a">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


    </div>
  );
}
